import { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../services/api';
import InactivityWarning from '../components/InactivityWarning';

const AuthContext = createContext();

// Inactivity timeout from .env (minutes), default 30 minutes
const INACTIVITY_TIMEOUT_MS =
  (parseInt(process.env.REACT_APP_INACTIVITY_TIMEOUT_MINUTES, 10) || 30) * 60 * 1000;

// Activity events to track
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

// Helper: decode JWT to get expiry
const getTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // convert to ms
  } catch {
    return null;
  }
};

// Helper: get storage (localStorage for remember me, sessionStorage otherwise)
const getStorage = () => {
  return localStorage.getItem('remember_me') === 'true' ? localStorage : sessionStorage;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  const inactivityTimer = useRef(null);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('user');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('remember_me');
      setUser(null);
      setShowInactivityWarning(false);
      window.location.href = '/login';
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      setShowInactivityWarning(true);
    }, INACTIVITY_TIMEOUT_MS);
  }, []);

  const startActivityTracking = useCallback(() => {
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, resetInactivityTimer, { passive: true })
    );
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const stopActivityTracking = useCallback(() => {
    ACTIVITY_EVENTS.forEach((event) =>
      window.removeEventListener(event, resetInactivityTimer)
    );
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
  }, [resetInactivityTimer]);

  useEffect(() => {
    checkAuth();
  }, []);

  // Start/stop activity tracking based on auth state
  useEffect(() => {
    if (user) {
      startActivityTracking();
    } else {
      stopActivityTracking();
    }
    return () => stopActivityTracking();
  }, [user, startActivityTracking, stopActivityTracking]);

  const checkAuth = async () => {
    try {
      // Check both storages (remember me uses localStorage)
      const storage = localStorage.getItem('remember_me') === 'true' ? localStorage : sessionStorage;
      const token = storage.getItem('access_token');
      const refreshToken = storage.getItem('refresh_token');

      if (token && refreshToken) {
        // Check if token is expired
        const expiry = getTokenExpiry(token);
        if (expiry && Date.now() > expiry) {
          // Token expired — try to refresh
          try {
            const response = await authAPI.refreshToken(refreshToken);
            const { access_token, refresh_token: new_refresh_token } = response.data;
            storage.setItem('access_token', access_token);
            storage.setItem('refresh_token', new_refresh_token);
          } catch {
            // Refresh failed — clear and go to login
            storage.removeItem('access_token');
            storage.removeItem('refresh_token');
            storage.removeItem('user');
            localStorage.removeItem('remember_me');
            setLoading(false);
            return;
          }
        }

        const userStr = storage.getItem('user');
        if (userStr) {
          const userData = JSON.parse(userStr);
          setUser(userData);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('user');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('remember_me');
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async () => {
    try {
      const storage = getStorage();
      const currentRefreshToken = storage.getItem('refresh_token');
      if (!currentRefreshToken) return;

      console.log('Refreshing token...');
      const response = await authAPI.refreshToken(currentRefreshToken);
      const { access_token, refresh_token } = response.data;

      storage.setItem('access_token', access_token);
      storage.setItem('refresh_token', refresh_token);
      console.log('✅ Token refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh token:', error);
      logout();
    }
  };

  const login = async (username, password, rememberMe = false) => {
    try {
      console.log('🔐 Attempting login for username:', username);
      const response = await authAPI.login({ username, password });
      console.log('✅ Login API response:', response.data);

      const { access_token, refresh_token, user: userData } = response.data;

      // Choose storage based on Remember Me
      const storage = rememberMe ? localStorage : sessionStorage;

      if (rememberMe) {
        localStorage.setItem('remember_me', 'true');
      } else {
        localStorage.removeItem('remember_me');
      }

      storage.setItem('access_token', access_token);
      storage.setItem('refresh_token', refresh_token);
      storage.setItem('user', JSON.stringify(userData));

      console.log('✅ User data stored:', userData);
      setUser(userData);

      return { success: true };
    } catch (error) {
      console.error('❌ Login failed - Full error:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error response data:', error.response?.data);
      console.error('❌ Error response status:', error.response?.status);
      console.error('❌ Error message:', error.message);

      const errorMessage = error.response?.data?.detail || error.message || 'Login failed';
      console.error('❌ Final error message shown to user:', errorMessage);

      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const handleStayLoggedIn = () => {
    setShowInactivityWarning(false);
    resetInactivityTimer();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refreshToken }}>
      {children}
      {showInactivityWarning && (
        <InactivityWarning
          onStayLoggedIn={handleStayLoggedIn}
          onLogout={logout}
        />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
